import type { FC } from 'react';
import { useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import type { TwitterCreativeFlow } from '../api';
import { haveSameFlows } from '../utils';

interface FlowsSelectProps {
  creativeName: string;
  value: TwitterCreativeFlow[];
  options: TwitterCreativeFlow[];
  optionsLoading?: boolean;
  /** The new flows are being saved: the select is locked and the arrow turns into a spinner. */
  loading?: boolean;
  onCommit: (flows: TwitterCreativeFlow[]) => void;
}

// Events are stopped so using the select does not move the grid focus.
const stop = (event: { stopPropagation: () => void }) => {
  event.stopPropagation();
};

/**
 * Flows of one creative. The backend replaces the whole set on every PATCH and wants one request per creative,
 * so picks made while the list is open are sent once, when it closes. Removing a chip of a closed list sends at once.
 */
const FlowsSelect: FC<FlowsSelectProps> = ({ creativeName, value, options, optionsLoading, loading, onCommit }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<TwitterCreativeFlow[] | null>(null);

  const commit = (next: TwitterCreativeFlow[]) => {
    setDraft(null);
    if (!haveSameFlows(next, value)) {
      onCommit(next.map(({ id, name }) => ({ id, name })));
    }
  };

  return (
    <Box
      sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}
      onClick={stop}
      onKeyDown={stop}
    >
      <Autocomplete<TwitterCreativeFlow, true, false, false>
        multiple
        disableCloseOnSelect
        size="small"
        limitTags={1}
        open={open}
        onOpen={() => {
          setOpen(true);
        }}
        onClose={() => {
          setOpen(false);
          if (draft) commit(draft);
        }}
        options={options}
        loading={optionsLoading}
        disabled={loading}
        value={draft ?? value}
        onChange={(_event, next) => {
          if (open) {
            setDraft(next);
          } else {
            commit(next);
          }
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        popupIcon={loading ? <CircularProgress size={16} aria-label="Saving flows" /> : undefined}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={(draft ?? value).length ? undefined : 'No flows'}
            inputProps={{ ...params.inputProps, 'aria-label': `Flows of ${creativeName}` }}
          />
        )}
        sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
      />
    </Box>
  );
};

export default FlowsSelect;
